import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { toast } from "sonner";
import { ArrowLeft, BarChart3, Star, Clock, Repeat, CheckCircle2, AlertTriangle } from "lucide-react";
import useUrlState from "../hooks/useUrlState";
import AvatarChip from "../components/ui/avatar-chip";
import { EmptyState } from "../components/ui/empty-state";
import Hint from "../components/Hint";

/**
 * Production / Performance — appraisal-grade metrics for creative roles.
 *
 * Phase 2: all four creative roles light up (editor, writer, director, DOP).
 * The compute is fully role-generic — same KPI strip, same per-person
 * table, same monthly snapshot frame — driven by ROLE_PERF_CONFIG on the
 * backend.
 *
 * The page is a clone of the Recruiting Performance pattern (proven UX):
 * window selector → totals strip → per-person ranked table.
 */

const ROLE_TABS = [
  { key: "editor", label: "Editors", phase: 1 },
  { key: "writer", label: "Writers", phase: 1 },
  { key: "director", label: "Directors", phase: 1 },
  { key: "dop", label: "DOPs", phase: 1 },
];

// What each role's main "thing" is called in copy. Drives column headers,
// the totals strip, and snapshot labels so the same UI can serve all four
// roles without per-role layouts.
const ROLE_NOUN = {
  editor:   { sing: "edit",    plur: "edits"    },
  writer:   { sing: "script",  plur: "scripts"  },
  director: { sing: "project", plur: "projects" },
  dop:      { sing: "shoot",   plur: "shoots"   },
};

const WINDOWS = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "all", label: "All time" },
];

// Top-level mode: live numbers vs frozen monthly snapshots vs the early-
// warning weekly Quality cadence. Three lenses on the same data.
const MODES = [
  { key: "live", label: "Live" },
  { key: "snapshots", label: "Monthly snapshots" },
  { key: "quality", label: "Quality cadence" },
];

export default function ProductionPerformance() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const myUid = user?.user_id || user?.id;

  const [role, setRole] = useUrlState("role", "editor");
  const [win, setWin] = useUrlState("win", "30d");
  const [mode, setMode] = useUrlState("mode", "live");
  const [data, setData] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [cadence, setCadence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSnapshot, setActiveSnapshot] = useState(null);
  const [expandedPid, setExpandedPid] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (mode === "live") {
        const r = await api.get(`/production/role-performance?role=${role}&window=${win}`);
        setData(r.data);
      } else if (mode === "snapshots") {
        const r = await api.get(`/production/role-snapshots?role=${role}`);
        setSnapshots(r.data || []);
      } else if (mode === "quality") {
        const r = await api.get(`/production/quality-cadence?role=${role}`);
        setCadence(r.data);
      }
    } catch (e) {
      console.error(e);
      toast.error("Couldn't load performance data");
    } finally {
      setLoading(false);
    }
  }, [role, win, mode]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const noun = ROLE_NOUN[role] || ROLE_NOUN.editor;

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 28px 80px", fontFamily: "var(--font-sans)" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <button
          onClick={() => navigate("/production")}
          style={{
            padding: "6px 12px",
            background: "var(--s1)",
            border: "1px solid var(--bdr)",
            borderRadius: 999,
            color: "var(--t2)",
            fontSize: 12,
            fontWeight: 500,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <ArrowLeft size={13} /> Production
        </button>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 600, color: "var(--t1)", letterSpacing: "-0.015em", margin: 0 }}>
            Performance
          </h1>
          <p style={{ color: "var(--t3)", fontSize: 13, marginTop: 4, margin: "4px 0 0" }}>
            Volume, quality, speed, reliability — the basis for monthly appraisal conversations.
          </p>
        </div>
      </div>

      {/* Role tabs */}
      <div className="dm-perf-role-tabs" style={{ display: "flex", gap: 6, marginBottom: 16, padding: 4, background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 999, width: "fit-content", maxWidth: "100%", overflowX: "auto", flexWrap: "nowrap" }}>
        {ROLE_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setRole(t.key)}
            style={{
              padding: "8px 18px",
              borderRadius: 999,
              border: "none",
              background: role === t.key ? "var(--s3)" : "transparent",
              color: role === t.key ? "var(--t1)" : "var(--t3)",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              transition: "all .15s",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Mode toggle: live vs snapshots */}
      <div className="dm-perf-mode-tabs" style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {MODES.map((m) => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: "6px 14px",
              borderRadius: 999,
              border: "1px solid var(--bdr)",
              background: mode === m.key ? "var(--t1)" : "var(--s1)",
              color: mode === m.key ? "var(--bg)" : "var(--t3)",
              fontSize: 12,
              fontWeight: mode === m.key ? 600 : 500,
              cursor: "pointer",
            }}
          >
            {m.label}
          </button>
        ))}
        <span style={{ marginLeft: 8, fontSize: 11, color: "var(--t4, #3e3e3e)" }}>
          {mode === "live"
            ? "Up-to-the-second numbers — what's happening now."
            : mode === "snapshots"
            ? "Frozen records — the basis for monthly appraisal conversations."
            : "Last week's craft, surfaced before the monthly snapshot freezes."}
          {mode === "snapshots" && (
            <Hint id="perf.snapshots">
              Frozen on the 1st of each month at 02:00 IST. Even if someone late-approves an asset days later, the snapshot stays as it was at month-end. That's how appraisals stay fair.
            </Hint>
          )}
          {mode === "quality" && (
            <Hint id="perf.quality_cadence">
              Different from monthly snapshots. Surfaces last week's craft so dips get caught before they show up in the formal review.
            </Hint>
          )}
        </span>
      </div>

      {/* Window selector — only relevant in live mode */}
      {mode === "live" && (
        <div style={{ display: "flex", gap: 6, marginBottom: 24, flexWrap: "wrap" }}>
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              onClick={() => setWin(w.key)}
              style={{
                padding: "6px 14px",
                borderRadius: 999,
                border: "1px solid var(--bdr)",
                background: win === w.key ? "var(--s3)" : "var(--s1)",
                color: win === w.key ? "var(--t1)" : "var(--t3)",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {w.label}
            </button>
          ))}
          {data?.window_label && (
            <span style={{ marginLeft: "auto", alignSelf: "center", fontSize: 12, color: "var(--t3)" }}>
              {data.window_label} · {totals.active_people || 0} active
            </span>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: "center", padding: 60, color: "var(--t3)" }}>Loading…</div>
      )}

      {/* ── Snapshots mode ── */}
      {!loading && mode === "snapshots" && (
        <SnapshotsView
          snapshots={snapshots}
          onOpenItem={setActiveSnapshot}
        />
      )}

      {/* ── Quality cadence mode ── */}
      {!loading && mode === "quality" && cadence && (
        <QualityCadenceView cadence={cadence} navigate={navigate} myUid={myUid} />
      )}

      {/* Snapshot detail drawer */}
      {activeSnapshot && (
        <SnapshotDetailDrawer
          snapshot={activeSnapshot}
          onClose={() => setActiveSnapshot(null)}
        />
      )}

      {/* Empty data — live */}
      {!loading && mode === "live" && rows.length === 0 && (
        <EmptyState
          icon={BarChart3}
          title="No deliveries in this window"
          message="When approved edits start landing inside the window, they'll show up here."
        />
      )}

      {/* Live data */}
      {!loading && mode === "live" && rows.length > 0 && (
        <>
          {/* KPI strip */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 12,
            marginBottom: 24,
          }}>
            <KpiCard
              icon={CheckCircle2}
              label={`${noun.plur[0].toUpperCase()}${noun.plur.slice(1)} delivered`}
              value={totals.edits_delivered ?? 0}
              tone="neutral"
            />
            <KpiCard
              icon={Star}
              label="Avg quality"
              value={fmtRating(totals.avg_quality_rating)}
              tone={ratingTone(totals.avg_quality_rating)}
              suffix="/ 5"
            />
            <KpiCard
              icon={CheckCircle2}
              label="First-review pass"
              value={fmtPct(totals.first_review_pass_rate)}
              tone={pctTone(totals.first_review_pass_rate, 0.6, 0.8)}
            />
            <KpiCard
              icon={Repeat}
              label="Avg revisions"
              value={totals.avg_revisions_per_edit ?? "—"}
              tone={revisionTone(totals.avg_revisions_per_edit)}
              suffix={`per ${noun.sing}`}
            />
            <KpiCard
              icon={Clock}
              label="On-time"
              value={fmtPct(totals.on_time_rate)}
              tone={pctTone(totals.on_time_rate, 0.6, 0.85)}
            />
          </div>

          {/* Per-person table */}
          <div style={{
            background: "var(--s1)",
            border: "1px solid var(--bdr)",
            borderRadius: 14,
            overflow: "hidden",
          }}>
            <div className="dm-perf-header" style={tableHeaderStyle}>
              <div>{ROLE_TABS.find(t => t.key === role)?.label?.replace(/s$/, "") || "Person"}</div>
              <div style={{ textAlign: "left" }}>Trend{data?.series_days ? ` · ${data.series_days}d` : ""}</div>
              <div style={{ textAlign: "right" }}>Delivered</div>
              <div style={{ textAlign: "right" }}>Volume</div>
              <div style={{ textAlign: "right" }}>Quality</div>
              <div style={{ textAlign: "right" }}>1st-pass</div>
              <div style={{ textAlign: "right" }}>Revisions</div>
              <div style={{ textAlign: "right" }}>Turnaround</div>
              <div style={{ textAlign: "right" }}>On-time</div>
              <div></div>
            </div>
            {rows.map((r) => {
              const isMe = r.person_id === myUid;
              const isExpanded = expandedPid === r.person_id;
              const series = r.daily_deliveries_series || [];
              const seriesTotal = series.reduce((a, b) => a + b, 0);
              return (
                <React.Fragment key={r.person_id}>
                  <div
                    className="dm-perf-row"
                    onClick={() => setExpandedPid(isExpanded ? null : r.person_id)}
                    style={{
                      ...tableRowStyle,
                      background: isMe ? "rgba(96,165,250,.06)" : "transparent",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                      <AvatarChip name={r.person_name} size={28} />
                      <span style={{ fontSize: 14, color: "var(--t1)", fontWeight: isMe ? 600 : 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.person_name}{isMe && <span style={{ marginLeft: 6, fontSize: 11, color: "#60a5fa" }}>(you)</span>}
                      </span>
                    </div>
                    <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center" }}>
                      <DailyDeliveryBars values={series} title={`${seriesTotal} ${noun.plur} · last ${series.length} days`} />
                    </div>
                    <NumCell value={r.edits_delivered} sub={r.edits_in_progress > 0 ? `+${r.edits_in_progress} active` : null} />
                    <NumCell value={r.weighted_volume} sub="weighted" />
                    <NumCell
                      value={fmtRating(r.avg_quality_rating)}
                      sub={craftBreakdown(r) || (r.rating_count > 0 ? `${r.rating_count} rating${r.rating_count === 1 ? "" : "s"}` : "—")}
                      accent={ratingTone(r.avg_quality_rating)}
                    />
                    <NumCell value={fmtPct(r.first_review_pass_rate)} accent={pctTone(r.first_review_pass_rate, 0.6, 0.8)} />
                    <NumCell value={r.avg_revisions_per_edit ?? "—"} accent={revisionTone(r.avg_revisions_per_edit)} />
                    <NumCell value={r.avg_turnaround_days != null ? `${r.avg_turnaround_days}d` : "—"} />
                    <NumCell value={fmtPct(r.on_time_rate)} accent={pctTone(r.on_time_rate, 0.6, 0.85)} />
                    <div style={{
                      color: "var(--t3)",
                      fontSize: 11,
                      transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                      transition: "transform .15s",
                      textAlign: "center",
                    }}>▶</div>
                  </div>
                  {isExpanded && (
                    <DailyDeliveryBreakdown rows={r.daily || []} totalDays={data?.series_days || series.length} nounSing={noun.sing} nounPlur={noun.plur} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Phase-2 engagement strip — only renders when at least one editor
              has engagement data, so the section disappears cleanly until
              outcome metrics start being entered. */}
          {(rows || []).some((r) => r.avg_er_rate != null || r.avg_shares != null || r.avg_saves != null) && (
            <div style={{ marginTop: 14, padding: "10px 16px", border: "1px solid var(--bdr)", borderRadius: 10, background: "var(--s1)" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--t3)", letterSpacing: ".05em", textTransform: "uppercase", marginBottom: 8 }}>
                Engagement — last {data?.window_label || "30 days"}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr 1fr", gap: 8, fontSize: 12, color: "var(--t2)" }}>
                <div style={{ fontWeight: 600 }}>{ROLE_TABS.find(t => t.key === role)?.label?.replace(/s$/, "") || "Person"}</div>
                <div>Avg shares</div>
                <div>Avg saves</div>
                <div>Avg ER%</div>
                {rows.filter((r) => r.avg_er_rate != null || r.avg_shares != null || r.avg_saves != null).map((r) => (
                  <React.Fragment key={`eng-${r.person_id}`}>
                    <div>{r.person_name}</div>
                    <div>{r.avg_shares ?? "—"}</div>
                    <div>{r.avg_saves ?? "—"}</div>
                    <div>{r.avg_er_rate != null ? `${r.avg_er_rate}%` : "—"}</div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Footnote */}
          <div style={{ marginTop: 16, fontSize: 11, color: "var(--t4, #3e3e3e)", lineHeight: 1.6 }}>
            Volume is weighted: ad film 3·, reel 2·, carousel 1.5·, story / static 1·, thumbnail 0.5·. Quality folds together legacy 1–5 review ratings AND the unified Hook / Craft / Fit rubric (rated by the asset's collaborators once it's Live — the same scores the 6-monthly review reads). Engagement (shares/saves/ER) comes from manual entry on the asset detail page once it's Live. First-pass = % of edits where the very first review was an approve. Turnaround is from first edit assignment to first approval.
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════ Helpers ═══════════════════════ */

const tableHeaderStyle = {
  display: "grid",
  gridTemplateColumns: "1.6fr 90px .7fr .7fr .8fr .8fr .8fr .9fr .8fr 18px",
  gap: 8,
  padding: "10px 16px",
  borderBottom: "1px solid var(--bdr)",
  fontSize: 11,
  color: "var(--t3)",
  fontWeight: 600,
  letterSpacing: ".04em",
  textTransform: "uppercase",
  background: "var(--s2)",
  minWidth: 820,
};

const tableRowStyle = {
  display: "grid",
  gridTemplateColumns: "1.6fr 90px .7fr .7fr .8fr .8fr .8fr .9fr .8fr 18px",
  gap: 8,
  padding: "12px 16px",
  borderBottom: "1px solid var(--bdr)",
  alignItems: "center",
  minWidth: 820,
};

function KpiCard({ icon: Icon, label, value, tone = "neutral", suffix }) {
  const tones = {
    red: { color: "#ef4444", bg: "rgba(239,68,68,.08)" },
    amber: { color: "#f59e0b", bg: "rgba(245,158,11,.08)" },
    green: { color: "#22c55e", bg: "rgba(34,197,94,.08)" },
    neutral: { color: "var(--t1)", bg: "var(--s1)" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <div style={{
      padding: "14px 16px",
      background: t.bg,
      border: "1px solid var(--bdr)",
      borderRadius: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--t3)", fontWeight: 500 }}>
        {Icon && <Icon size={12} />}
        {label}
      </div>
      <div style={{ marginTop: 6, fontSize: 22, fontWeight: 600, color: t.color, letterSpacing: "-0.02em" }}>
        {value}
        {suffix && <span style={{ fontSize: 12, color: "var(--t3)", fontWeight: 500, marginLeft: 4 }}>{suffix}</span>}
      </div>
    </div>
  );
}

function NumCell({ value, sub, accent }) {
  const tones = {
    red: "#ef4444", amber: "#f59e0b", green: "#22c55e", neutral: "var(--t1)",
  };
  const color = tones[accent] || "var(--t1)";
  return (
    <div style={{ textAlign: "right" }}>
      <div style={{ fontSize: 14, fontWeight: 600, color, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "var(--t4, #3e3e3e)", marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

function fmtPct(v) {
  if (v == null || isNaN(v)) return "—";
  return `${Math.round(v * 100)}%`;
}

function fmtRating(v) {
  if (v == null || isNaN(v)) return "—";
  return Number(v).toFixed(1);
}

// Compact craft-dimension breakdown for the Quality cell sub-line. Renders
// "Hook4.2 Craft3.8 Fit4.0" — the unified Hook/Craft/Fit pillars, only when at
// least one has data; falls through to the plain rating count otherwise.
function craftBreakdown(r) {
  const dims = [
    ["Hook", r.avg_hook],
    ["Craft", r.avg_craft],
    ["Fit", r.avg_fit],
  ].filter(([, v]) => v != null);
  if (dims.length === 0) return null;
  return dims.map(([k, v]) => `${k}${Number(v).toFixed(1)}`).join(" ");
}

// Tone helpers — green/amber/red coloring on KPI cards + per-row cells.
function pctTone(v, amberThreshold, greenThreshold) {
  if (v == null) return "neutral";
  if (v >= greenThreshold) return "green";
  if (v >= amberThreshold) return "amber";
  return "red";
}

function ratingTone(v) {
  if (v == null) return "neutral";
  if (v >= 4) return "green";
  if (v >= 3) return "amber";
  return "red";
}

function revisionTone(v) {
  if (v == null) return "neutral";
  // Inverse: lower is better
  if (v <= 1) return "green";
  if (v <= 2) return "amber";
  return "red";
}

/* ═══════════════════════ Snapshots view ═══════════════════════ */

/**
 * Frozen monthly snapshots — grouped by month, then by person.
 * Click a person row → drawer with their per-edit breakdown for that month.
 */
function SnapshotsView({ snapshots, onOpenItem }) {
  // Group by month so the most recent month sits at the top.
  const byMonth = useMemo(() => {
    const m = {};
    for (const s of snapshots) {
      const key = s.month || "—";
      if (!m[key]) m[key] = [];
      m[key].push(s);
    }
    // Sort each month's people by weighted volume desc
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => (b.metrics?.weighted_volume || 0) - (a.metrics?.weighted_volume || 0));
    }
    // Return as ordered array of [month, people[]] tuples
    return Object.entries(m).sort(([a], [b]) => b.localeCompare(a));
  }, [snapshots]);

  if (snapshots.length === 0) {
    return (
      <EmptyState
        icon={BarChart3}
        title="No snapshots yet"
        message="The first monthly snapshot lands automatically on the 1st of next month at 02:00 IST. Or trigger one now via POST /production/role-snapshots/run."
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {byMonth.map(([month, people]) => (
        <div key={month}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
            <h2 style={{ fontSize: 17, fontWeight: 600, color: "var(--t1)", letterSpacing: "-0.01em", margin: 0 }}>
              {monthLabel(month)}
            </h2>
            <span style={{ fontSize: 12, color: "var(--t3)" }}>
              {people.length} {people.length === 1 ? "person" : "people"}
            </span>
          </div>
          <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, overflow: "hidden" }}>
            {people.map((s) => {
              const m = s.metrics || {};
              return (
                <div
                  key={s.snapshot_id}
                  onClick={() => onOpenItem(s)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.6fr .6fr .8fr .6fr .8fr .8fr .8fr",
                    gap: 8,
                    padding: "12px 16px",
                    borderBottom: "1px solid var(--bdr)",
                    cursor: "pointer",
                    alignItems: "center",
                    minWidth: 820,
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "var(--s2)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <AvatarChip name={s.person_name} size={28} />
                    <span style={{ fontSize: 14, fontWeight: 500, color: "var(--t1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.person_name}
                    </span>
                  </div>
                  <NumCell value={m.edits_delivered ?? 0} sub="delivered" />
                  <NumCell value={m.weighted_volume ?? 0} sub="volume" />
                  <NumCell value={fmtRating(m.avg_quality_rating)} accent={ratingTone(m.avg_quality_rating)} />
                  <NumCell value={fmtPct(m.first_review_pass_rate)} accent={pctTone(m.first_review_pass_rate, 0.6, 0.8)} sub="1st-pass" />
                  <NumCell value={m.avg_revisions_per_edit ?? "—"} accent={revisionTone(m.avg_revisions_per_edit)} sub="rev/edit" />
                  <NumCell value={fmtPct(m.on_time_rate)} accent={pctTone(m.on_time_rate, 0.6, 0.85)} sub="on-time" />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Drawer that opens when you click a snapshot row. Shows the person + month
 * + per-edit breakdown, sourced ENTIRELY from the snapshot record (not live)
 * so re-opening months later returns the same numbers no matter what's
 * happened to the underlying assets since.
 */
function SnapshotDetailDrawer({ snapshot, onClose }) {
  const m = snapshot.metrics || {};
  const items = snapshot.items || [];
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.5)",
        zIndex: 90,
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 100vw)",
          height: "100vh",
          background: "var(--s1)",
          borderLeft: "1px solid var(--bdr)",
          padding: 24,
          overflowY: "auto",
          fontFamily: "var(--font-sans)",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 18 }}>
          <AvatarChip name={snapshot.person_name} size={44} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: "var(--t1)" }}>{snapshot.person_name}</div>
            <div style={{ fontSize: 13, color: "var(--t3)", marginTop: 2 }}>
              {monthLabel(snapshot.month)} · {snapshot.role} snapshot
            </div>
            <div style={{ fontSize: 11, color: "var(--t4, #3e3e3e)", marginTop: 4 }}>
              Frozen at {snapshot.generated_at ? new Date(snapshot.generated_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
              {snapshot.generated_by === "system" ? " by automated cron" : ""}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "var(--t3)", cursor: "pointer", fontSize: 18 }}>✕</button>
        </div>

        {/* Metrics summary */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
          <SnapMetric label="Delivered" value={m.edits_delivered ?? 0} />
          <SnapMetric label="Volume" value={m.weighted_volume ?? 0} suffix="weighted" />
          <SnapMetric label="Quality" value={fmtRating(m.avg_quality_rating)} suffix={`/ 5 · ${m.rating_count || 0} ratings`} accent={ratingTone(m.avg_quality_rating)} />
          <SnapMetric label="1st-pass" value={fmtPct(m.first_review_pass_rate)} accent={pctTone(m.first_review_pass_rate, 0.6, 0.8)} />
          <SnapMetric label="Revisions" value={m.avg_revisions_per_edit ?? "—"} suffix="per edit" accent={revisionTone(m.avg_revisions_per_edit)} />
          <SnapMetric label="Turnaround" value={m.avg_turnaround_days != null ? `${m.avg_turnaround_days}d` : "—"} />
          <SnapMetric label="On-time" value={fmtPct(m.on_time_rate)} accent={pctTone(m.on_time_rate, 0.6, 0.85)} />
        </div>

        {/* Per-edit breakdown */}
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--t3)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
          Edits this month · {items.length}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.length === 0 && (
            <div style={{ fontSize: 13, color: "var(--t3)", padding: 12 }}>No edits delivered.</div>
          )}
          {items.map((it) => (
            <div
              key={it.asset_id}
              style={{
                padding: "10px 12px",
                background: "var(--s2)",
                border: "1px solid var(--bdr)",
                borderRadius: 10,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 500, color: "var(--t1)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {it.title || "Untitled"}
                </span>
                <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "var(--s3)", color: "var(--t3)", textTransform: "capitalize" }}>
                  {it.kind || "—"}
                </span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, fontSize: 11, color: "var(--t3)" }}>
                {it.quality_rating != null && (
                  <span style={{ color: it.quality_rating >= 4 ? "#22c55e" : it.quality_rating >= 3 ? "#f59e0b" : "#ef4444" }}>
                    Quality {it.quality_rating}/5
                  </span>
                )}
                <span>{it.revisions} rev{it.revisions === 1 ? "" : "s"}</span>
                {it.first_review_pass && <span style={{ color: "#22c55e" }}>1st-pass ✓</span>}
                {it.turnaround_days != null && <span>{it.turnaround_days}d turnaround</span>}
                {it.on_time === true && <span style={{ color: "#22c55e" }}>on-time</span>}
                {it.on_time === false && <span style={{ color: "#ef4444" }}>late</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SnapMetric({ label, value, suffix, accent }) {
  const tones = { red: "#ef4444", amber: "#f59e0b", green: "#22c55e", neutral: "var(--t1)" };
  const color = tones[accent] || "var(--t1)";
  return (
    <div style={{ padding: 12, background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 10 }}>
      <div style={{ fontSize: 10, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ marginTop: 4, fontSize: 18, fontWeight: 600, color, letterSpacing: "-0.015em" }}>
        {value}
        {suffix && <span style={{ fontSize: 11, color: "var(--t3)", fontWeight: 500, marginLeft: 4 }}>{suffix}</span>}
      </div>
    </div>
  );
}

function monthLabel(yyyymm) {
  if (!yyyymm) return "—";
  try {
    const [y, m] = yyyymm.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  } catch { return yyyymm; }
}

/* ═══════════════════════ Quality cadence view ═══════════════════════ */

/**
 * Three stacked panels:
 *   1. Per-editor strip — last week's avg quality + WoW delta + sparkline.
 *   2. Flagged edits — anything low-rated, over-revised, or late.
 *   3. Reviewer calibration (admin/manager only) — average rating each
 *      reviewer GAVE this week. Catches "always 5" or "always 3" drift.
 */
function QualityCadenceView({ cadence, navigate, myUid }) {
  const rows = cadence.rows || [];
  const flagged = cadence.flagged || [];
  const calibration = cadence.reviewer_calibration; // null if not admin/manager

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ fontSize: 13, color: "var(--t3)" }}>
        Week of <strong style={{ color: "var(--t1)" }}>{cadence.week_label}</strong> — captures last week's craft so the monthly snapshot doesn't surprise anyone.
      </div>

      {/* Panel 1: per-editor strip */}
      <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 14, overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--bdr)", fontSize: 11, fontWeight: 600, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Quality this week</span>
          <span style={{ color: "var(--t4, #3e3e3e)", fontSize: 10 }}>{rows.length} editor{rows.length === 1 ? "" : "s"} delivered</span>
        </div>
        {rows.length === 0 ? (
          <div style={{ padding: 28, textAlign: "center", color: "var(--t3)", fontSize: 13 }}>
            Nothing delivered last week. Check back after the next push.
          </div>
        ) : (
          rows.map((r) => {
            const isMe = r.editor_id === myUid;
            const delta = r.week_over_week_delta;
            const deltaTone = delta == null ? "neutral" : delta > 0 ? "green" : delta < 0 ? "amber" : "neutral";
            const tones = { red: "#ef4444", amber: "#f59e0b", green: "#22c55e", neutral: "var(--t3)" };
            return (
              <div key={r.editor_id} style={{
                display: "grid",
                gridTemplateColumns: "1.6fr .9fr 0.6fr 1.4fr 0.6fr",
                gap: 12,
                padding: "12px 16px",
                borderBottom: "1px solid var(--bdr)",
                alignItems: "center",
                background: isMe ? "rgba(96,165,250,.06)" : "transparent",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                  <AvatarChip name={r.name} size={28} />
                  <span style={{ fontSize: 14, fontWeight: 500, color: "var(--t1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.name}{isMe && <span style={{ marginLeft: 6, fontSize: 11, color: "#60a5fa" }}>(you)</span>}
                  </span>
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: tones[ratingTone(r.avg_quality)] }}>
                    {fmtRating(r.avg_quality)}<span style={{ fontSize: 11, color: "var(--t3)", fontWeight: 500, marginLeft: 4 }}>/ 5</span>
                  </div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: tones[deltaTone] }}>
                  {delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta}`}
                </div>
                <Sparkline values={r.sparkline} />
                <div style={{ fontSize: 12, color: "var(--t3)", textAlign: "right" }}>
                  {r.deliveries} edit{r.deliveries === 1 ? "" : "s"}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Panel 2: flagged edits */}
      {flagged.length > 0 && (
        <div style={{ background: "var(--s1)", border: "1px solid rgba(245,158,11,.3)", borderRadius: 14, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--bdr)", fontSize: 11, fontWeight: 600, color: "#f59e0b", textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={13} />
            Flagged edits — {flagged.length}
            <span style={{ color: "var(--t4, #3e3e3e)", fontSize: 10, marginLeft: "auto", fontWeight: 500 }}>
              Where to spend a 1:1 conversation
            </span>
          </div>
          {flagged.map((f) => (
            <div
              key={f.asset_id}
              onClick={() => navigate(`/production/assets/${f.asset_id}`)}
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid var(--bdr)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "var(--s2)"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: "var(--t1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {f.title || "Untitled"}
                </div>
                <div style={{ fontSize: 12, color: "var(--t3)", marginTop: 4, display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <span>by {f.editor_name}</span>
                  {f.flag_reasons.includes("low_quality") && (
                    <span style={{ color: "#ef4444" }}>Quality {f.quality_rating}/5</span>
                  )}
                  {f.flag_reasons.includes("many_revisions") && (
                    <span style={{ color: "#f59e0b" }}>{f.revisions} revisions</span>
                  )}
                  {f.flag_reasons.includes("late") && (
                    <span style={{ color: "#ef4444" }}>Late</span>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 11, color: "var(--t4, #3e3e3e)" }}>
                {f.delivered_at ? new Date(f.delivered_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—"}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Panel 3: reviewer calibration — admin/manager only */}
      {calibration && calibration.length > 0 && (
        <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 14, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--bdr)", fontSize: 11, fontWeight: 600, color: "var(--t3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Reviewer calibration
            <span style={{ color: "var(--t4, #3e3e3e)", marginLeft: 8, fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>
              · catches "always 5" or "always 3" drift before it skews appraisal
            </span>
          </div>
          {calibration.map((c) => (
            <div key={c.reviewer_id} style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--bdr)",
              display: "grid",
              gridTemplateColumns: "1.6fr 0.6fr 0.6fr",
              gap: 12,
              alignItems: "center",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <AvatarChip name={c.name} size={26} />
                <span style={{ fontSize: 14, color: "var(--t1)" }}>{c.name}</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--t3)", textAlign: "right" }}>
                {c.ratings_given} rating{c.ratings_given === 1 ? "" : "s"}
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, textAlign: "right", color: c.avg_rating_given == null ? "var(--t3)" : c.avg_rating_given >= 4.7 || c.avg_rating_given <= 2.5 ? "#f59e0b" : "var(--t1)" }}>
                {fmtRating(c.avg_rating_given)}<span style={{ fontSize: 11, color: "var(--t3)", fontWeight: 500, marginLeft: 4 }}>avg</span>
              </div>
            </div>
          ))}
          <div style={{ padding: "10px 16px", fontSize: 11, color: "var(--t4, #3e3e3e)" }}>
            Reviewers consistently giving ≥ 4.7 or ≤ 2.5 highlighted — worth a calibration check.
          </div>
        </div>
      )}
    </div>
  );
}

/* DailyDeliveryBars — count-based daily volume bars for the live-mode trend
   column. Different from the quality `Sparkline` below (which is rating-based,
   green/amber/red coloured). Bars scale to the row's own max so a writer
   delivering 1/day still gets a readable shape.

   Empty days render a thin neutral tick so the gap is visible (script writers
   often have multi-day silent stretches between deliveries — that pattern is
   itself the signal). */
function DailyDeliveryBars({ values, color = "#60a5fa", title }) {
  if (!values || values.length === 0) {
    return <span style={{ fontSize: 12, color: "var(--t3)" }}>—</span>;
  }
  const max = Math.max(1, ...values);
  const w = 84, h = 22, gap = 1;
  const barW = Math.max(1, (w - gap * (values.length - 1)) / values.length);
  return (
    <svg width={w} height={h} style={{ display: "block" }} aria-label={title}>
      <title>{title}</title>
      {values.map((v, i) => {
        const bh = v === 0 ? 2 : Math.max(2, (v / max) * h);
        return (
          <rect
            key={i}
            x={i * (barW + gap)}
            y={h - bh}
            width={barW}
            height={bh}
            fill={v === 0 ? "var(--t4, #888)" : color}
            opacity={v === 0 ? 0.35 : 1}
            rx={1}
          />
        );
      })}
    </svg>
  );
}

function DailyDeliveryBreakdown({ rows, totalDays, nounSing, nounPlur }) {
  if (!rows || rows.length === 0) {
    return (
      <div style={{
        padding: "14px 16px 18px 56px",
        borderBottom: "1px solid var(--bdr)",
        background: "var(--s2)",
        fontSize: 12,
        color: "var(--t3)",
      }}>
        No {nounPlur || "deliveries"} in the last {totalDays} days.
      </div>
    );
  }
  return (
    <div style={{
      padding: "10px 16px 14px 56px",
      borderBottom: "1px solid var(--bdr)",
      background: "var(--s2)",
    }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: "140px 100px",
        gap: 8,
        padding: "6px 0",
        fontSize: 10,
        color: "var(--t3)",
        fontWeight: 600,
        letterSpacing: ".04em",
        textTransform: "uppercase",
        borderBottom: "1px solid var(--bdr)",
      }}>
        <div>Date</div>
        <div style={{ textAlign: "right" }}>{nounPlur || "Delivered"}</div>
      </div>
      {rows.map((d) => (
        <div key={d.date} style={{
          display: "grid",
          gridTemplateColumns: "140px 100px",
          gap: 8,
          padding: "8px 0",
          fontSize: 13,
          color: "var(--t2)",
          borderBottom: "1px dashed var(--bdr)",
        }}>
          <div style={{ color: "var(--t1)", fontWeight: 500 }}>
            {fmtBreakdownDate(d.date)}
          </div>
          <div style={{ textAlign: "right", color: "var(--t1)", fontWeight: 600 }}>
            {d.delivered} {d.delivered === 1 ? (nounSing || "delivery") : (nounPlur || "deliveries")}
          </div>
        </div>
      ))}
    </div>
  );
}

function fmtBreakdownDate(ymd) {
  // ymd like "2026-05-03". Returns "Sat, 3 May".
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const wk = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][dt.getUTCDay()];
  const mn = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][m - 1];
  return `${wk}, ${d} ${mn}`;
}

/* Sparkline — mini bar chart of avg-quality-per-week for the last 8 weeks.
   Bars sized by rating (1-5), null buckets render as empty. Compact enough
   to live inside a table row without dominating it. */
function Sparkline({ values }) {
  // 8 buckets, each [0..5] or null
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 24 }}>
      {values.map((v, i) => {
        const heightPct = v == null ? 0 : Math.max(8, (v / 5) * 100);
        const tone = v == null ? "transparent" : v >= 4 ? "#22c55e" : v >= 3 ? "#f59e0b" : "#ef4444";
        const isCurrent = i === values.length - 1;
        return (
          <div
            key={i}
            title={v == null ? "no deliveries" : `${v}/5`}
            style={{
              width: 6,
              height: `${heightPct}%`,
              background: tone,
              border: v == null ? "1px solid var(--bdr)" : "none",
              opacity: isCurrent ? 1 : 0.6,
              borderRadius: 2,
            }}
          />
        );
      })}
    </div>
  );
}
